/**
 * Utility functions for managing the onboarding tour
 */

export const tourSteps = [
  "Profile", // 0 - Start with profile update
  "Properties", // 1 - Then properties page
  "Tenants", // 2 - After creating properties/houses
  "Payments", // 3 - After creating tenants
  "Invoices", // 4 - After opening payments
  "Expenses", // 5 - After opening invoices
  "Overview", // 6 - Finally overview
];

/**
 * Get current tour step from localStorage
 * @returns {number} Current tour step (0-6)
 */
export const getCurrentTourStep = () => {
  return parseInt(localStorage.getItem("tourStep")) || 0;
};

/**
 * Set tour step in localStorage
 * @param {number} step - Tour step to set (0-6)
 */
export const setTourStep = (step) => {
  if (step >= 0 && step < tourSteps.length) {
    localStorage.setItem("tourStep", step.toString());
  }
};

/**
 * Check if user is in first-time tour mode
 * @returns {boolean} True if in tour mode
 */
export const isInTourMode = () => {
  return JSON.parse(localStorage.getItem("isFirstTime")) === true;
};

/**
 * Start the tour (set isFirstTime to true and reset to step 0)
 */
export const startTour = () => {
  localStorage.setItem("isFirstTime", "true");
  localStorage.setItem("tourStep", "0");
};

/**
 * Complete the tour (set isFirstTime to false and remove tourStep)
 */
export const completeTour = () => {
  localStorage.setItem("isFirstTime", "false");
  localStorage.removeItem("tourStep");
};

/**
 * Advance to next tour step
 * Can be called from any component when user completes an action
 */
export const advanceTour = () => {
  const currentStep = getCurrentTourStep();
  const nextStep = currentStep + 1;

  if (nextStep < tourSteps.length) {
    setTourStep(nextStep);

    // Trigger navigation update if the function is available
    if (window.advanceRentlyTour) {
      window.advanceRentlyTour();
    }

    return true; // Tour continues
  } else {
    completeTour();
    return false; // Tour completed
  }
};

/**
 * Skip current tour step and advance to next step
 * Can be used for steps that don't require mandatory actions
 */
export const skipTourStep = () => {
  return advanceTour();
};

/**
 * Get tour step description
 * @param {number} step - Tour step number
 * @returns {string} Description for the step
 */
export const getTourStepDescription = (step) => {
  const descriptions = {
    0: "Update your profile information to proceed to Properties",
    1: "Create your first property and add houses to proceed to Tenants",
    2: "Add your first tenant to proceed to Payments",
    3: "Check how payments work in your system",
    4: "Learn about invoices and billing",
    5: "Manage your expenses and costs",
    6: "View your dashboard overview",
  };

  return descriptions[step] || "";
};

/**
 * Check if current step requires completion before advancing
 * @param {number} step - Tour step number
 * @returns {boolean} True if step requires completion
 */
export const stepRequiresCompletion = (step) => {
  // Steps 0, 1, 2 require completion before advancing
  // Steps 3, 4, 5, 6 can be skipped
  return step >= 0 && step <= 2;
};

/**
 * Check if a navigation item should be visible based on current tour step
 * @param {string} itemName - Name of navigation item
 * @returns {boolean} True if item should be visible
 */
export const shouldShowNavItem = (itemName) => {
  if (!isInTourMode()) return true;

  const currentStep = getCurrentTourStep();
  const itemStep = tourSteps.indexOf(itemName);

  // Always show Profile, Logout, and Messages
  if (["Profile", "Logout", "Messages"].includes(itemName)) {
    return true;
  }

  // Show items up to current step
  return itemStep !== -1 && itemStep <= currentStep;
};

/**
 * Example usage in other components:
 *
 * // When user creates a property successfully:
 * import { advanceTour, isInTourMode } from '../utils/tourUtils';
 *
 * const handlePropertyCreated = () => {
 *   // Your property creation logic here...
 *
 *   // Advance tour if in tour mode
 *   if (isInTourMode()) {
 *     advanceTour();
 *   }
 * };
 *
 * // When user adds a tenant:
 * const handleTenantAdded = () => {
 *   // Your tenant creation logic here...
 *
 *   if (isInTourMode()) {
 *     advanceTour();
 *   }
 * };
 */
